/**
 * The Stock tab's expanded row (Stock idea 4): where the item is, who booked it, which sites still need
 * it and its last movements — with Issue to site / Transfer / Reserve / Adjust… / Edit in Catalog
 * opening the right form already filled in. "Adjust…" (Stock idea 5) records an Adjustment movement
 * with a required reason instead of typing a new balance over the old one.
 */
import { useMemo, useState } from 'react';
import { ArrowRightLeft, Check, Info, Lock, Minus, Pencil, Plus, Printer, Scale, Truck, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusPill } from '@/components/ui/status-pill';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { LinkButton, PersonAvatar, UpperLabel } from './InventoryUi';
import ScannedStrip from './ScannedStrip';
import { QbItemFacts } from './QuickBooksStock';
import {
  INVENTORY_LOCATIONS,
  MOVEMENT_KIND_LABEL,
  MOVEMENT_KIND_TONE,
  documentLabel,
  formatQty,
  groupMovements,
  binOf,
  locationColor,
  pcsLabel,
  signedQty,
  toQty
} from './inventoryModel';

export const ADJUST_REASONS = ['Count correction', 'Damaged', 'Found'];

export default function StockItemDetail({
  item,
  reservations = [],
  demand = [],
  movements = [],
  onIssue,
  onTransfer,
  onReserve,
  onEditCatalog,
  onFixItem,
  onAdjust,
  onPrintLabel,
  onSetBin,
  scanned = null,
  qbItem = null,
  qbReadAt = '',
  canEditCatalog = true,
  canEdit = true
}) {
  const [adjusting, setAdjusting] = useState(false);
  const partNumber = item.part_number;
  const available = Math.max(0, toQty(item.available_qty));
  const onHand = Math.max(0, toQty(item.on_hand_qty));
  const locations = useMemo(() => {
    const quantities = item.location_quantities || {};
    const known = INVENTORY_LOCATIONS.map((location) => ({ location, qty: toQty(quantities[location]) }));
    Object.keys(quantities).forEach((location) => {
      if (!INVENTORY_LOCATIONS.includes(location)) known.push({ location, qty: toQty(quantities[location]) });
    });
    return known.sort((left, right) => right.qty - left.qty);
  }, [item.location_quantities]);
  const lastDocuments = useMemo(() => groupMovements(movements).slice(0, 3), [movements]);

  return (
    <div className="pl-9" data-testid={`stock-detail-${partNumber}`}>
      {scanned ? <div className="mb-3 max-w-[420px]"><ScannedStrip scanned={scanned} /></div> : null}
      <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2 xl:grid-cols-4">
        <section aria-label="By location">
          <UpperLabel className="mb-2">By location</UpperLabel>
          <ul className="space-y-1">
            {locations.map(({ location, qty }) => (
              <li key={location} className={cn('flex min-h-[22px] flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px]', qty > 0 ? 'font-semibold text-ink' : 'text-ink-3')}>
                <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: qty > 0 ? locationColor(location) : 'var(--line)' }} aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{location}</span>
                {qty > 0 || binOf(item, location) ? (
                  <ShelfTag partNumber={partNumber} location={location} value={binOf(item, location)} canEdit={canEdit && Boolean(onSetBin)} onSave={onSetBin} />
                ) : null}
                <span className="tabular-nums">{formatQty(qty)}</span>
              </li>
            ))}
          </ul>
        </section>

        <section aria-label="Bookings">
          <UpperLabel className="mb-2">Bookings</UpperLabel>
          {reservations.length ? (
            <ul className="space-y-1.5">
              {reservations.map((reservation) => (
                <li key={reservation.id} className="text-[12.5px]">
                  <span className="font-semibold text-ink">{reservation.project_name || 'Unknown project'}</span>
                  <span className="text-ink-2"> · {pcsLabel(reservation.qty_reserved)}</span>
                  {reservation.needed_by_date ? <span className="block text-[11.5px] text-ink-3">Needed by {formatDate(reservation.needed_by_date, { year: 'auto' })}</span> : null}
                </li>
              ))}
            </ul>
          ) : (
            <>
              <p className="text-[12.5px] text-ink-3">No active bookings</p>
              {canEdit && available > 0 ? <LinkButton className="mt-1 text-[12.5px]" onClick={() => onReserve?.()}>Reserve for a project</LinkButton> : null}
            </>
          )}
        </section>

        <section aria-label="Needed on sites">
          <UpperLabel className="mb-2">Needed on sites</UpperLabel>
          {demand.length ? (
            <>
              <ul className="space-y-1">
                {demand.slice(0, 4).map((entry) => (
                  <li key={entry.project_id} className="flex items-center gap-2 text-[12.5px]">
                    <span className="min-w-0 flex-1 truncate font-semibold text-ink">{entry.project_name}</span>
                    <span className="tabular-nums text-ink-2">{pcsLabel(entry.remaining_qty)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-[11.5px] text-ink-3">
                {demand.length > 4 ? `+${demand.length - 4} more · ` : ''}Remaining BOQ{demand.some((entry) => toQty(entry.booked_qty) > 0) ? '' : ' · not booked'}
              </p>
            </>
          ) : (
            <p className="text-[12.5px] text-ink-3">No site is waiting for it</p>
          )}
          {toQty(item.lpo_remaining) > 0 ? (
            // Site demand counts the Sites BOQ; what is already ordered (LPO lines still to arrive) is shown here.
            <p className="mt-1.5 text-[12px] font-semibold text-status-blue-ink" data-testid={`stock-detail-incoming-${partNumber}`}>
              Incoming on LPO: {pcsLabel(item.lpo_remaining)}
            </p>
          ) : null}
        </section>

        <section aria-label="Last movements">
          <UpperLabel className="mb-2">Last movements</UpperLabel>
          {lastDocuments.length ? (
            <ul className="space-y-2">
              {lastDocuments.map((group) => {
                const line = group.movements[0];
                const qty = group.neutral ? formatQty(line.qty) : `${signedQty(line) > 0 ? '+' : '−'}${formatQty(Math.abs(signedQty(line)))}`;
                return (
                  <li key={group.key} className="text-[12.5px]">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <StatusPill tone={MOVEMENT_KIND_TONE[group.kind]} label={MOVEMENT_KIND_LABEL[group.kind]} size="sm" />
                      <span className="font-semibold text-ink">{documentLabel(group)}</span>
                      <b className={cn('font-bold tabular-nums', signedQty(line) > 0 ? 'text-status-green-ink' : 'text-ink')}>{qty}</b>
                    </span>
                    <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-ink-3">
                      {formatDate(group.date)}
                      {group.user ? <><span aria-hidden="true">·</span><PersonAvatar name={group.user} size={16} /> {group.user}</> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-[12.5px] text-ink-3">No movements yet</p>
          )}
        </section>
      </div>

      {/* QuickBooks Desktop's record of this part, read only — only when QuickBooks has it. */}
      <QbItemFacts qbItem={qbItem} here={toQty(item.on_hand_qty)} readAt={qbReadAt} wide className="mt-4 bg-surface" testId={`stock-detail-qb-${partNumber}`} />

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-line-2 pt-3">
        <span className="mr-auto inline-flex items-center gap-1.5 text-[12px] text-ink-3">
          <Info className="h-3.5 w-3.5" aria-hidden="true" /> {canEdit ? 'Every stock change is recorded as a movement' : 'View only — your role cannot change stock'}
        </span>
        {canEdit ? (
        <>
        {/* Booked stock can still be issued to the project that booked it. */}
        <Button type="button" size="sm" variant="outline" className="text-ink" disabled={onHand <= 0} onClick={() => onIssue?.()} data-testid={`stock-detail-issue-${partNumber}`}>
          <Truck aria-hidden="true" /> Issue to site
        </Button>
        <Button type="button" size="sm" variant="outline" className="text-ink" disabled={available <= 0} onClick={() => onTransfer?.()}>
          <ArrowRightLeft aria-hidden="true" /> Transfer
        </Button>
        <Button type="button" size="sm" variant="outline" className="text-ink" disabled={available <= 0} onClick={() => onReserve?.()}>
          <Lock aria-hidden="true" /> Reserve
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={cn('text-ink', adjusting && 'border-[color:var(--brand-soft-border)] bg-brand-soft text-brand-ink')}
          aria-expanded={adjusting}
          onClick={() => setAdjusting((open) => !open)}
          data-testid={`stock-adjust-${partNumber}`}
        >
          <Scale aria-hidden="true" /> Adjust…
        </Button>
        </>
        ) : null}
        {onPrintLabel ? (
          <Button type="button" size="sm" variant="ghost" className="text-ink-2" onClick={() => onPrintLabel()} data-testid={`stock-print-label-${partNumber}`} title="Barcode label for the shelf or box — scan it to open this item">
            <Printer aria-hidden="true" /> Shelf label
          </Button>
        ) : null}
        {item.catalog_id ? (
          <Button type="button" size="sm" variant="ghost" className="text-ink-2" onClick={() => onEditCatalog?.()} data-testid={`stock-edit-catalog-${partNumber}`}>
            <Pencil aria-hidden="true" /> {canEditCatalog ? 'Edit in Catalog' : 'Open in Catalog'}
          </Button>
        ) : canEdit ? (
          <Button type="button" size="sm" variant="ghost" className="text-ink-2" onClick={() => onFixItem?.()}>
            <Pencil aria-hidden="true" /> Fix part no. / description
          </Button>
        ) : null}
      </div>

      {canEdit && adjusting ? (
        <AdjustStockForm
          item={item}
          locations={locations}
          onCancel={() => setAdjusting(false)}
          onSave={async (payload) => { await onAdjust?.(payload); setAdjusting(false); }}
        />
      ) : null}
    </div>
  );
}

/** Location · Change (− / +) · On hand a → b · Reason (required). Saves an Adjustment movement. */
export function AdjustStockForm({ item, locations, onCancel, onSave }) {
  const firstWithStock = locations.find((entry) => entry.qty > 0)?.location || INVENTORY_LOCATIONS[0];
  const [location, setLocation] = useState(firstWithStock);
  const [change, setChange] = useState('-1');
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const onHand = toQty(locations.find((entry) => entry.location === location)?.qty);
  const delta = Number(change);
  const valid = Number.isFinite(delta) && delta !== 0;
  const after = valid ? onHand + delta : onHand;
  const finalReason = reason === 'Other' ? otherReason.trim() : reason;
  const step = (by) => setChange((value) => String((Number(value) || 0) + by));

  const submit = async (event) => {
    event.preventDefault();
    if (!valid) { setError('Enter how many pieces to add (+) or remove (−)'); return; }
    if (after < 0) { setError(`Only ${formatQty(onHand)} in ${location} — the count cannot go below 0`); return; }
    if (!finalReason) { setError('Pick a reason — it is saved with the movement'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({ part_number: item.part_number, location, qty: delta, reason: finalReason });
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the adjustment'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-3 rounded-xl border border-line bg-surface px-4 py-3 shadow-card" data-testid="stock-adjust-form">
      <p className="flex flex-wrap items-center gap-x-2 text-[13px] text-ink">
        <Scale className="h-4 w-4 text-ink-3" aria-hidden="true" />
        <b className="font-bold">Adjust stock</b>
        <span className="text-[12.5px] text-ink-3">· saved as an <b className="font-semibold text-ink-2">Adjustment</b> movement with your name and reason — the balance is never typed over</span>
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-x-4 gap-y-3">
        <label className="block">
          <span className="mb-1 block text-[12px] font-semibold text-ink-3">Location</span>
          <select
            value={location}
            onChange={(event) => setLocation(event.target.value)}
            className="h-9 min-w-[180px] rounded-control border border-line bg-surface px-2.5 text-[13px] text-ink outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="stock-adjust-location"
          >
            {locations.map((entry) => <option key={entry.location} value={entry.location}>{entry.location} ({formatNumber(entry.qty)})</option>)}
          </select>
        </label>
        <div>
          <span className="mb-1 block text-[12px] font-semibold text-ink-3" id="adjust-change-label">Change</span>
          <div className="flex h-9 items-stretch overflow-hidden rounded-control border border-line bg-surface">
            <button type="button" className="grid w-9 place-items-center text-ink-2 hover:bg-surface-hover" onClick={() => step(-1)} aria-label="One less"><Minus className="h-3.5 w-3.5" aria-hidden="true" /></button>
            <Input
              type="number"
              step="any"
              value={change}
              onChange={(event) => setChange(event.target.value)}
              aria-labelledby="adjust-change-label"
              className={cn('h-full w-16 rounded-none border-0 border-x border-line text-center font-bold shadow-none focus-visible:ring-0', delta < 0 ? 'text-status-red-ink' : 'text-status-green-ink')}
              data-testid="stock-adjust-change"
            />
            <button type="button" className="grid w-9 place-items-center text-ink-2 hover:bg-surface-hover" onClick={() => step(1)} aria-label="One more"><Plus className="h-3.5 w-3.5" aria-hidden="true" /></button>
          </div>
        </div>
        <div>
          <span className="mb-1 block text-[12px] font-semibold text-ink-3">On hand</span>
          <span className="flex h-9 items-center gap-1.5 text-[13px] tabular-nums text-ink-2" aria-live="polite" data-testid="stock-adjust-preview">
            {formatQty(onHand)} → <b className={cn('text-[15px] font-black', after < 0 ? 'text-status-red-ink' : 'text-ink')}>{formatQty(after)}</b>
          </span>
        </div>
        <fieldset className="min-w-0">
          <legend className="mb-1 text-[12px] font-semibold text-ink-3">Reason · <span className="text-status-red-ink">required</span></legend>
          <div className="flex flex-wrap items-center gap-2">
            {/* Phones: a 2 × 2 grid of 44px chips; wider: one segmented pill group. */}
            <div className="grid w-full grid-cols-2 gap-2 sm:inline-flex sm:w-auto sm:flex-wrap sm:gap-0 sm:rounded-control sm:border sm:border-line sm:bg-surface-2 sm:p-0.5" role="radiogroup" aria-label="Reason">
              {[...ADJUST_REASONS, 'Other'].map((option) => (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={reason === option}
                  onClick={() => setReason(option)}
                  className={cn(
                    'h-11 whitespace-nowrap rounded-[10px] border px-3 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:h-8 sm:rounded-[8px] sm:border-transparent sm:text-[12.5px]',
                    reason === option
                      ? 'border-brand bg-[color:var(--navy-50)] text-brand-ink sm:bg-surface sm:text-ink sm:shadow-card'
                      : 'border-line bg-surface text-ink-2 hover:text-ink sm:bg-transparent sm:text-ink-3'
                  )}
                  data-testid={`stock-adjust-reason-${option.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {option === 'Other' ? 'Other…' : option}
                </button>
              ))}
            </div>
            {reason === 'Other' ? (
              <Input
                value={otherReason}
                onChange={(event) => setOtherReason(event.target.value)}
                placeholder="e.g. Stock count 30 Sep"
                autoFocus
                maxLength={300}
                className="h-9 w-56"
                aria-label="Other reason"
                data-testid="stock-adjust-other"
              />
            ) : null}
          </div>
        </fieldset>
        <div className="ml-auto flex items-center gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="stock-adjust-save">{saving ? 'Saving…' : 'Save adjustment'}</Button>
        </div>
      </div>
      {error ? <p className="mt-2 text-[12.5px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
    </form>
  );
}

/** The shelf / bin of an item in one store (18 #10) — printed on its label. Click to set or change it. */
function ShelfTag({ partNumber, location, value = '', canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const slug = location.toLowerCase().replace(/\s+/g, '-');
  const start = () => { setDraft(value); setError(''); setEditing(true); };
  const save = async () => {
    const next = draft.replace(/\s+/g, ' ').trim();
    if (next === value) { setEditing(false); return; }
    setBusy(true);
    setError('');
    try {
      await onSave?.({ part_number: partNumber, location, bin: next });
      setEditing(false);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the shelf'));
    } finally {
      setBusy(false);
    }
  };
  if (editing) {
    return (
      <span className="order-last flex w-full flex-col pl-3.5">
        <span className="flex items-center gap-1">
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') { event.preventDefault(); save(); }
              if (event.key === 'Escape') { event.preventDefault(); setEditing(false); }
            }}
            maxLength={40}
            autoFocus
            placeholder="e.g. Shelf B3"
            aria-label={`Shelf in ${location}`}
            className="h-7 min-w-0 flex-1 px-2 text-[12px] font-normal"
            data-testid={`stock-shelf-input-${slug}`}
          />
          <Button type="button" size="icon-xs" variant="outline" className="h-7 w-7 text-ink" disabled={busy} onClick={save} aria-label="Save shelf" data-testid={`stock-shelf-save-${slug}`}><Check aria-hidden="true" /></Button>
          <Button type="button" size="icon-xs" variant="ghost" className="h-7 w-7 text-ink-3" onClick={() => setEditing(false)} aria-label="Cancel"><X aria-hidden="true" /></Button>
        </span>
        {error ? <span className="mt-0.5 text-[11px] font-semibold text-status-red-ink" role="alert">{error}</span> : null}
      </span>
    );
  }
  if (!canEdit) {
    return value ? <span className="rounded border border-line bg-surface px-1.5 text-[11px] font-semibold text-ink-2">{value}</span> : null;
  }
  return (
    <button
      type="button"
      onClick={start}
      className={cn(
        'rounded border px-1.5 text-[11px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        value ? 'border-line bg-surface text-ink-2 hover:border-[color:var(--brand-ink)]' : 'border-dashed border-[color:var(--line-dashed)] text-ink-3 hover:text-brand-ink'
      )}
      title={value ? `Shelf in ${location} — click to change` : `Set the shelf in ${location}`}
      data-testid={`stock-shelf-${slug}`}
    >
      {value || '+ Shelf'}
    </button>
  );
}
